import React, {createContext, useContext, useState} from 'react';
import {useGlobalContext} from '../../../../common/components/core/GlobalContext';
import {useOrderInvoiceFormContext} from '../OrderInvoiceFormContext';
import {useGlobalMessageContext} from '../../../../common/components/core/GlobalMessageContext';
import {useMessage} from '../../../../common/components/core/useGlobalMessage';
import {shallowEqual, useSelector} from 'react-redux';
import {RootState} from '../../../../store/configureStore';

export type PopupReplaceInvoiceContext = {
  show: boolean,
  handleShow: () => void,
  handleClose: () => void,
  handleCloseAll: () => void,
  handleReplaceInvoice: (invoiceNum: string, customerEmail: string, datetime: string) => Promise<void>
}

export interface PopupReplaceInvoiceContextProviderProps {
  children: React.ReactNode;
}

export const defaultContext: PopupReplaceInvoiceContext = {
  show: false,
  handleShow: () => {},
  handleClose: () => {},
  handleCloseAll: () => {},
  handleReplaceInvoice: async () => {},
};

export const PopupReplaceInvoiceContext = createContext<PopupReplaceInvoiceContext>(defaultContext);
PopupReplaceInvoiceContext.displayName = 'PopupReplaceInvoiceContext';

export const usePopupReplaceInvoiceContext = () => useContext(PopupReplaceInvoiceContext);

export const PopupReplaceInvoiceContextProvider = ({children}: PopupReplaceInvoiceContextProviderProps) => {
  const [show, setShow] = useState<boolean>(false);
  const handleShow = () => setShow(true);
  const handleClose = () => setShow(false);

  const handleCloseAll = () => {
    closePopupSelectAction();
    handleClose();
  };

  const user = useSelector((state: RootState) => state.auth.user, shallowEqual);
  const agentName = [user?.profile?.firstName, user?.profile?.lastName].filter(Boolean).join(' ');

  const globalMessage = useGlobalMessageContext();
  const M = useMessage();
  const {apiService} = useGlobalContext();
  const {closePopupSelectAction, handleSubmit} = useOrderInvoiceFormContext();

  const handleReplaceInvoice = async (
    invoiceNum: string,
    customerEmail: string,
    datetime: string
  ) => {
    try {
      const result = await apiService.cancelInvoice(
        invoiceNum,
        customerEmail,
        `בוצע ביטול עקב שינוי החשבונית לבקשת ${agentName}`,
        new Date(datetime)
      );

      if (result && result?.errorCode === 0) {
        await handleSubmit();
      } else {
        globalMessage.open(M.serverError);
      }
    } catch (e) {
      console.error(e);
      globalMessage.open(M.serverError);
    } finally {
      handleCloseAll();
    }
  };

  const context: PopupReplaceInvoiceContext = {
    ...defaultContext,
    show,
    handleShow,
    handleClose,
    handleCloseAll,
    handleReplaceInvoice
  };

  return (
    <PopupReplaceInvoiceContext.Provider value={context}>
      {children}
    </PopupReplaceInvoiceContext.Provider>
  );
};
